import React from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import Iconify from 'src/components/iconify';

import { UI } from '../constants';

// Opens the calculator dialog by setting #calculator in the URL.
// NewDesign listens for that hash, so the same link also works from
// WhatsApp / social posts: https://safiswap.com/#calculator
export const CALCULATOR_HASH = '#calculator';

const pillBaseSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  cursor: 'pointer',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
  transition: 'all 0.15s ease',
  '&:focus-visible': { outline: `2px solid ${UI.green}`, outlineOffset: 2 },
};

// Same pill as "See how it works" in the hero.
const outlinedSx = {
  ...pillBaseSx,
  height: 46,
  px: 2.5,
  borderRadius: 6,
  border: `1px solid ${UI.borderStrong}`,
  bgcolor: 'transparent',
  color: UI.text,
  fontSize: 14.5,
  fontWeight: 700,
  '& .calc-icon': { color: UI.green },
  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', borderColor: UI.green },
};

const linkSx = {
  ...pillBaseSx,
  px: 0,
  py: 0.5,
  border: 0,
  bgcolor: 'transparent',
  color: UI.green,
  fontSize: 14,
  fontWeight: 700,
  textDecoration: 'underline',
  textUnderlineOffset: 4,
  '&:hover': { opacity: 0.85 },
};

// Gradient pill, used inside the banner.
const gradientPillSx = {
  ...pillBaseSx,
  height: 46,
  px: 2.75,
  border: 0,
  borderRadius: 6,
  color: '#0A0B08',
  background: UI.gradient,
  fontSize: 14.5,
  fontWeight: 800,
  '&:hover': { filter: 'brightness(1.05)' },
};

function renderIcon() {
  return <Iconify className="calc-icon" icon="solar:calculator-bold" width={18} />;
}

function renderArrow() {
  return <Iconify icon="solar:arrow-right-linear" width={18} />;
}

function renderBanner({ label, onClick, sx }) {
  return (
    <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 8 }, ...sx }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          border: `1px solid ${alpha(UI.green, 0.35)}`,
          bgcolor: UI.panel,
          px: { xs: 2.5, md: 4 },
          py: { xs: 2.75, md: 3 },
          boxShadow: `0 0 40px ${alpha(UI.green, 0.08)}`,
          '&::after': {
            content: '""',
            position: 'absolute',
            right: -120,
            top: -120,
            width: 320,
            height: 320,
            background: `radial-gradient(circle, ${alpha(UI.green, 0.14)} 0%, transparent 65%)`,
            pointerEvents: 'none',
          },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 2.25, md: 4 }}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                color: UI.green,
                bgcolor: alpha(UI.green, 0.1),
                border: `1px solid ${alpha(UI.green, 0.3)}`,
              }}
            >
              <Iconify icon="solar:calculator-bold" width={24} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: UI.mono,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: UI.green,
                }}
              >
                Remittance calculator
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: { xs: 17, md: 19 },
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: UI.text,
                  lineHeight: 1.3,
                }}
              >
                Buying ETH in MetaMask to send home? See what arrives in Somalia.
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: 13.5, color: UI.textMuted }}>
                Card or Google Pay purchase, network fee and SafiSwap fee included.
              </Typography>
            </Box>
          </Stack>

          <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{ ...gradientPillSx, width: { xs: '100%', md: 'auto' } }}
          >
            {label}
            {renderArrow()}
          </Box>
        </Stack>
      </Box>
    </Container>
  );
}

function getVariantSx(variant) {
  if (variant === 'link') return linkSx;
  return outlinedSx;
}

export default function CalculatorButton({
  variant = 'outlined',
  label = 'How much will they receive?',
  sx,
}) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const handleClick = () => {
    navigate({ pathname, search, hash: CALCULATOR_HASH });
  };

  // Full-width strip between the hero and "How it works". Aimed at one flow:
  // a sender buying ETH inside MetaMask and sending it to Somalia.
  if (variant === 'banner') {
    return renderBanner({ label: 'Calculate payout', onClick: handleClick, sx });
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      sx={{ ...getVariantSx(variant), ...sx }}
    >
      {renderIcon()}
      {label}
    </Box>
  );
}

CalculatorButton.propTypes = {
  variant: PropTypes.oneOf(['outlined', 'link', 'banner']),
  label: PropTypes.string,
  sx: PropTypes.object,
};
